import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import apiService from '../../services/apiService';

interface LegalGateModalProps {
  open: boolean;
  onClose: () => void;
  onAccept: () => void;
  contextType: 'ride' | 'request' | 'bid';
  contextId: string;
}

const LegalGateModal: React.FC<LegalGateModalProps> = ({
  open,
  onClose,
  onAccept,
  contextType,
  contextId
}) => {
  const [userInitials, setUserInitials] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAccept = async () => {
    if (!userInitials.trim()) {
      setError('Please enter your initials');
      return;
    }

    if (userInitials.length < 1 || userInitials.length > 10) {
      setError('Initials must be between 1 and 10 characters');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const response: any = await apiService.post('/chat-consent/accept', {
        contextType,
        contextId,
        userInitials: userInitials.toUpperCase().trim()
      });

      if (response.success) {
        onAccept();
      } else {
        setError(response.error || response.message || 'Failed to accept consent');
      }
    } catch (err: any) {
      console.error('Error accepting consent:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to accept consent';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAccept();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" sx={{ fontSize: '2.25rem' }} />
          <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.875rem' }}>
            Important: Chat Guidelines
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="warning" sx={{ mb: 3, '& .MuiAlert-message': { fontSize: '1.5rem' } }}>
          <Typography variant="body1" paragraph sx={{ fontSize: '1.5rem' }}>
            <strong>This chat is for ride/bid logistics only.</strong>
          </Typography>
          <Typography variant="body2" paragraph sx={{ fontSize: '1.3125rem' }}>
            • Sharing phone numbers or private info is at your own risk
          </Typography>
          <Typography variant="body2" paragraph sx={{ fontSize: '1.3125rem' }}>
            • Ride Club is not liable for off-platform interactions
          </Typography>
          <Typography variant="body2" paragraph sx={{ fontSize: '1.3125rem' }}>
            • <strong>Do not text and drive</strong> — it is illegal and unsafe
          </Typography>
        </Alert>

        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: '1.3125rem' }}>
          By continuing, you acknowledge that you have read and understood these guidelines.
        </Typography>

        <Box sx={{ mt: 3 }}>
          <Typography variant="body2" fontWeight="bold" gutterBottom sx={{ fontSize: '1.3125rem' }}>
            Enter your initials to confirm:
          </Typography>
          <TextField
            fullWidth
            placeholder="e.g., JD"
            value={userInitials}
            onChange={(e) => {
              setUserInitials(e.target.value.toUpperCase());
              setError(null);
            }}
            onKeyPress={handleKeyPress}
            error={Boolean(error)}
            helperText={error}
            autoFocus
            sx={{
              '& .MuiInputBase-input': { fontSize: '1.5rem' },
              '& .MuiInputLabel-root': { fontSize: '1.3125rem' },
              '& .MuiFormHelperText-root': { fontSize: '1.125rem' }
            }}
            inputProps={{
              maxLength: 10,
              style: { textTransform: 'uppercase' }
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleAccept}
          variant="contained"
          color="primary"
          disabled={!userInitials.trim() || submitting}
          fullWidth
          sx={{ fontSize: '1.5rem', py: 1.5 }}
        >
          {submitting ? 'Processing...' : 'I Understand & Accept'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LegalGateModal;

